<template>
  <div class="app-field" :class="{ 'app-field-invalido': Boolean(error) }">
    <label class="app-field-label" :for="controlId">
      {{ label }}<span v-if="required" class="app-field-req" aria-hidden="true"> *</span>
    </label>
    <slot :control-id="controlId">
      <input
        :id="controlId"
        class="app-field-control"
        :type="type"
        :value="value"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :min="min"
        :max="max"
        :inputmode="inputmode"
        :autocomplete="autocomplete"
        :aria-invalid="error ? 'true' : undefined"
        :aria-describedby="error ? controlId + '-error' : undefined"
        :data-testid="testid"
        @input="onInput"
        @blur="$emit('blur')"
      />
    </slot>
    <p
      v-if="error"
      :id="controlId + '-error'"
      class="app-field-error"
      role="alert"
      :data-testid="testid ? testid + '-error' : null"
    >
      {{ error }}
    </p>
  </div>
</template>

<script>
/*
 * AppField — campo de formulario con etiqueta persistente y error en línea.
 *
 * Pieza base de los formularios migrados (reemplaza v-text-field con rules).
 * - Por defecto renderiza un <input>; con slot se puede dar otro control
 *   (p. ej. <select>) y el slot recibe `controlId` para ligarlo al label.
 * - `uppercase` replica la directiva v-uppercase del sistema viejo.
 * - `format` aplica una máscara mientras se teclea (teléfono, fecha): el
 *   texto del input se reescribe aquí porque Vue no actualiza el value de un
 *   input enfocado cuando el padre cambia el modelo.
 * - El error se muestra junto al campo (guía visual: validación en línea).
 *
 * Uso: <AppField v-model="form.nombre" label="Nombre" required :error="err" />
 */
let consecutivo = 0

export default {
  name: 'AppField',
  props: {
    value: { type: [String, Number], default: '' },
    label: { type: String, required: true },
    type: { type: String, default: 'text' },
    placeholder: { type: String, default: '' },
    error: { type: String, default: '' },
    required: { type: Boolean, default: false },
    uppercase: { type: Boolean, default: false },
    format: { type: Function, default: null },
    maxlength: { type: [String, Number], default: null },
    min: { type: String, default: null },
    max: { type: String, default: null },
    inputmode: { type: String, default: null },
    autocomplete: { type: String, default: 'off' },
    testid: { type: String, default: null },
  },
  data() {
    return { controlId: `app-field-${++consecutivo}` }
  },
  methods: {
    onInput(event) {
      let valor = event.target.value
      if (this.uppercase) valor = valor.toUpperCase()
      if (this.format) valor = this.format(valor)
      if (valor !== event.target.value) event.target.value = valor
      this.$emit('input', valor)
    },
  },
}
</script>

<style scoped>
.app-field-label {
  display: block;
  margin-bottom: 6px;
  color: #3d4b5f;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
}
.app-field-req {
  color: #b91c1c;
}
.app-field-control {
  display: block;
  width: 100%;
  min-height: 44px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #d8e0ea;
  border-radius: 12px;
  background: #fff;
  color: #0b1b2e;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  transition: border-color 0.16s ease, outline-color 0.16s ease;
}
.app-field-control::placeholder {
  color: #94a3b3;
}
.app-field-control:focus {
  border-color: #0a4f9c;
  outline: 3px solid #7ab9f2;
  outline-offset: 1px;
}
/* Controles que llegan por slot (p. ej. <select>) */
::v-deep .app-field-control {
  display: block;
  width: 100%;
  min-height: 44px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #d8e0ea;
  border-radius: 12px;
  background: #fff;
  color: #0b1b2e;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  transition: border-color 0.16s ease, outline-color 0.16s ease;
}
::v-deep .app-field-control:focus {
  border-color: #0a4f9c;
  outline: 3px solid #7ab9f2;
  outline-offset: 1px;
}
.app-field-invalido .app-field-control,
.app-field-invalido ::v-deep .app-field-control {
  border-color: #b91c1c;
}
.app-field-invalido .app-field-control:focus,
.app-field-invalido ::v-deep .app-field-control:focus {
  outline-color: #f2a6aa;
}
.app-field-error {
  margin: 4px 0 0;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;
}
</style>
